import React, { createContext, useState, useRef, useCallback, ReactNode, useContext } from 'react';

interface LoadingContextProps {
    isLoading: boolean;
    setLoading: (loading: boolean) => void;
}

const LoadingContext = createContext<LoadingContextProps | undefined>(undefined);

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
    const pendingCount = useRef(0);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [visible, setVisible] = useState(false);

    const setLoading = useCallback((loading: boolean) => {
        if (loading) {
            pendingCount.current += 1;
            // Only start the debounce timer when going from 0 → 1
            if (pendingCount.current === 1 && !debounceTimer.current) {
                debounceTimer.current = setTimeout(() => {
                    debounceTimer.current = null;
                    if (pendingCount.current > 0) setVisible(true);
                }, 300);
            }
        } else {
            pendingCount.current = Math.max(0, pendingCount.current - 1);
            if (pendingCount.current === 0) {
                // Cancel pending show timer if requests finished before 300ms
                if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                    debounceTimer.current = null;
                }
                setVisible(false);
            }
        }
    }, []);

    return (
        <LoadingContext.Provider value={{ isLoading: visible, setLoading }}>
            {children}
        </LoadingContext.Provider>
    );
};

export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (!context) {
        throw new Error('useLoading must be used within a LoadingProvider');
    }
    return context;
};
